import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { toast } from '@/components/core/toaster';
import { queryClient } from '@/lib/query-client';
import { agentsKeys } from './use-agents';
import { fieldsKeys } from './use-fields';
import { groupsKeys } from './use-groups';
import { contactsKeys } from './use-contacts';
import { usersKeys } from './use-users';
import { accountUsersKeys } from './use-account-users';
import { campaignsKeys } from './use-campaigns';
import { callLogsKeys } from './use-call-logs';
import { phoneNumbersKeys } from './use-phone-numbers';
import { pluginsKeys } from './use-plugins';
import { systemLogsKeys } from './use-system-logs';
import { dashboardKeys } from './use-dashboard';
import { keyVaultKeys } from './use-key-vault';
import { toolsKeys } from './use-tools';
import { conversationTemplatesKeys } from './use-conversation-templates';

export interface Account {
  id: string;
  name: string;
  enabled: boolean;
  type?: "smb" | "enterprise";
}

export const accountsKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountsKeys.all, 'list'] as const,
  list: (query?: string) => [...accountsKeys.lists(), { query }] as const,
  details: () => [...accountsKeys.all, 'detail'] as const,
  detail: (id: string) => [...accountsKeys.details(), id] as const,
  info: () => [...accountsKeys.all, 'info'] as const,
};

export function useAccountsList(query?: string, enabled = true) {
  return useQuery({
    queryKey: accountsKeys.list(query),
    queryFn: async () => {
      const response = await apiClient.get(`/accounts/available`);
      return response.data?.data;
    },
    enabled: enabled,
  });
}

export function useAccount(id: string) {
  return useQuery({
    queryKey: accountsKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`/accounts/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Account>) => {
      const response = await apiClient.post('/accounts', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
      toast.success('Account created successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Account creation failed');
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Account> }) => {
      const response = await apiClient.put(`/accounts/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(accountsKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
      toast.success('Account updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Account update failed');
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`/accounts/${id}`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: accountsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
      toast.success('Account deleted successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Account deletion failed');
    },
  });
}

export function useImpersonateAccount() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post(`/accounts/${id}/use`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: accountUsersKeys.all });
      queryClient.removeQueries({ queryKey: agentsKeys.all });
      queryClient.removeQueries({ queryKey: callLogsKeys.all });
      queryClient.removeQueries({ queryKey: campaignsKeys.all });
      queryClient.removeQueries({ queryKey: contactsKeys.all });
      queryClient.removeQueries({ queryKey: fieldsKeys.all });
      queryClient.removeQueries({ queryKey: groupsKeys.all });
      queryClient.removeQueries({ queryKey: phoneNumbersKeys.all });
      queryClient.removeQueries({ queryKey: pluginsKeys.all });
      queryClient.removeQueries({ queryKey: systemLogsKeys.all });
      queryClient.removeQueries({ queryKey: usersKeys.all });
      queryClient.removeQueries({ queryKey: dashboardKeys.all });
      queryClient.removeQueries({ queryKey: keyVaultKeys.all });  
      queryClient.removeQueries({ queryKey: toolsKeys.all });
      queryClient.removeQueries({ queryKey: conversationTemplatesKeys.all });
      // The business app's state belongs to the account just left.
      queryClient.removeQueries({ queryKey: ['onboarding'] });
      queryClient.removeQueries({ queryKey: ['number-verification'] });
      queryClient.removeQueries({ queryKey: ['billing'] });
      queryClient.removeQueries({ queryKey: ['number-pool'] });
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error(error.response.data.message || 'Something went wrong!');
    },
  });
}

export function useAccountsInfo() {
	return useQuery({
		queryKey: accountsKeys.info(),
		queryFn: async () => {
			const response = await apiClient.get(`/accounts/info`);
			return response.data;
		},
		refetchOnMount: "always",
	});
}

export function useDeactivateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.put(`/accounts/${id}`, { enabled: false });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
      toast.success('Account deactivated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error(error.response.data.message || 'Something went wrong!');
    },
  });
}

export function useActivateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.put(`/accounts/${id}`, { enabled: true });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
      toast.success('Account activated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error(error.response.data.message || 'Something went wrong!');
    },
  });
}